import React, { useEffect } from 'react'
import { Button, MenuItem } from '@material-ui/core'
import { injectIntl } from 'react-intl'
import { Field, Form } from 'react-final-form'
import { connect } from 'react-redux'
import ifWindowWidthLessThan from 'helpers/ifWindowWidthLessThan'
import getValidator, { VALIDATOR_IS_EMPTY } from 'validation'
import Input from 'components/common/Form/Input'
import {
  fetchProjectedSitesManagerClients,
  fetchProjectedSitesManagerRegions,
  fetchProjectedSitesManagerList,
  fetchProjectedSitesManagerSites,
  fetchProjectedSitesManagerSpecies,
  fetchProjectedSitesManagerProductFamily,
  saveListForm,
  setActiveSite,
} from 'reducers/projectedSitesManager/reducer'
import withPromise from 'helpers/withPromise'
import { Select } from 'components/common/FormInputs'
import { ONE_YEAR_IN_MONTHES, SITE_TYPE_SEAWATER } from 'constants/index'
import MonthYearSelect from './MonthYearSelect'
import messages from '../translate'

function EditForm({
  intl: { formatMessage },
  activeSite,
  onPanelClose,
  isEditMode,
  clientsList,
  sitesList,
  regionsList,
  speciesList,
  productFamilyList,
  fetchProjectedSitesManagerClients,
  fetchProjectedSitesManagerRegions,
  fetchProjectedSitesManagerSpecies,
  fetchProjectedSitesManagerProductFamily,
  fetchProjectedSitesManagerList,
  fetchProjectedSitesManagerSites,
  siteType,
  snapshot,
  setActiveSite,
  saveListForm,
  storePsfs,
}) {
  const { harvestingMonthYear, stockingMonthYear, canUpdate } = activeSite
  const fullWidth = ifWindowWidthLessThan(599)

  useEffect(() => {
    fetchProjectedSitesManagerClients(siteType)

    if (siteType === SITE_TYPE_SEAWATER) {
      fetchProjectedSitesManagerRegions()
      fetchProjectedSitesManagerSpecies()
      fetchProjectedSitesManagerProductFamily()
    }
  }, [])

  const formatDigitValue = value => {
    if (value === undefined || value === null || value === '0') return ''

    const lastKey = String(value).slice(-1)
    const oldValue = String(value).slice(0, -1)

    if (/^[0-9]$/.test(lastKey)) {
      return value
    }

    return oldValue
  }

  const onFieldKeyUp = ({ target: { value } }, fieldName) => {
    setActiveSite({ [fieldName]: value })
  }

  const renderNumberInput = (fieldName, required = true) => (
    <Field
      required={required}
      name={fieldName}
      type="text"
      label={formatMessage(messages[fieldName])}
      component={Input}
      className="form-control"
      onKeyUp={event => onFieldKeyUp(event, fieldName)}
      format={formatDigitValue}
      validate={required && getValidator(VALIDATOR_IS_EMPTY)}
    />
  )

  const convertToNumber = param => (param ? Number(param) : undefined)

  const onSubmitForm = ({
    id,
    client,
    siteId,
    region,
    specieId,
    productFamilyId,
    stockingMonthYear,
    stockingFishCount,
    stockingWeight,
    monthsFromStocking,
    numberOfCages,
    harvestingMonthYear,
    harvestingLength,
    yearClass,
    enabled,
    canUpdate,
  }) => {
    let data = {
      harvestingMonthYear,
      harvestingLength: convertToNumber(harvestingLength),
    }

    if (canUpdate || !isEditMode) {
      data = {
        ...data,
        enabled: isEditMode ? enabled : true,
        clientId: !isEditMode ? client : undefined,
        siteId: !isEditMode ? siteId : undefined,
        region: !isEditMode ? region : undefined,
        specieId,
        productFamilyId,
        stockingMonthYear,
        yearClass: convertToNumber(yearClass),
        stockingFishCount: convertToNumber(stockingFishCount),
        stockingWeight: convertToNumber(stockingWeight),
        monthsFromStocking: convertToNumber(monthsFromStocking),
        numberOfCages: convertToNumber(numberOfCages),
      }
    }

    withPromise(saveListForm, {
      data,
      id,
      sucessMessage: formatMessage(messages.siteUpdatedSuccessfully),
      isEditMode,
      siteType,
      snapshot,
    }).then(() => {
      onPanelClose()
      fetchProjectedSitesManagerList({
        psfs: storePsfs,
        siteType,
        snapshot,
      })
    })
  }

  const onChangeSelect = (value, field, needsFetchSites) => {
    if (value && activeSite[field] !== value) {
      setActiveSite({ [field]: value })

      if (needsFetchSites) {
        setActiveSite({ siteId: null })
        fetchProjectedSitesManagerSites({
          clientId: value,
          siteType,
        })
      }
    }

    return value
  }

  return (
    <Form
      onSubmit={onSubmitForm}
      initialValues={activeSite}
      render={({ handleSubmit }) => (
        <form onSubmit={handleSubmit} className="event-details-form">
          {!isEditMode && (
            <>
              <div className="form-group">
                <Field
                  required
                  name="client"
                  component={Select}
                  label={formatMessage(messages.clientName)}
                  formControlProps={{
                    fullWidth: true,
                    required: true,
                  }}
                  validate={getValidator([VALIDATOR_IS_EMPTY])}
                  parse={value => onChangeSelect(value, 'client', true)}>
                  {clientsList.map(({ clientId, client }) => (
                    <MenuItem key={clientId} value={clientId}>
                      {client}
                    </MenuItem>
                  ))}
                </Field>
              </div>
              <div className="form-group">
                <Field
                  required
                  name="siteId"
                  component={Select}
                  label={formatMessage(messages.siteSIEP)}
                  formControlProps={{
                    fullWidth: true,
                    required: true,
                  }}
                  validate={getValidator([VALIDATOR_IS_EMPTY])}
                  parse={value => onChangeSelect(value, 'siteId')}
                  disabled={!sitesList.size}>
                  {sitesList.map(({ siteSiep, siteId }) => (
                    <MenuItem key={siteId} value={siteId}>
                      {siteSiep}
                    </MenuItem>
                  ))}
                </Field>
              </div>
              <div className="form-group">
                <Field
                  required
                  name="region"
                  component={Select}
                  label={formatMessage(messages.region)}
                  formControlProps={{
                    fullWidth: true,
                    required: true,
                  }}
                  validate={getValidator([VALIDATOR_IS_EMPTY])}
                  parse={value => onChangeSelect(value, 'region')}>
                  {regionsList.map(({ region }) => (
                    <MenuItem key={region} value={region}>
                      {region}
                    </MenuItem>
                  ))}
                </Field>
              </div>
            </>
          )}
          {((isEditMode && canUpdate) || !isEditMode) && (
            <>
              <div className="form-group">
                <Field
                  required
                  name="specieId"
                  component={Select}
                  label={formatMessage(messages.specie)}
                  formControlProps={{
                    fullWidth: true,
                    required: true,
                  }}
                  validate={getValidator([VALIDATOR_IS_EMPTY])}
                  parse={value => onChangeSelect(value, 'specieId')}>
                  {speciesList.map(({ id, specie }) => (
                    <MenuItem key={id} value={id}>
                      {specie}
                    </MenuItem>
                  ))}
                </Field>
              </div>
              {renderNumberInput('yearClass')}
              <MonthYearSelect
                fieldName="stockingMonthYear"
                activeDate={stockingMonthYear}
                dateToCompare={harvestingMonthYear}
                numberOfMonthBeforeDate={ONE_YEAR_IN_MONTHES}
                numberOfMonthAfterDate={ONE_YEAR_IN_MONTHES * 3}
              />
              {renderNumberInput('stockingFishCount')}
              {renderNumberInput('stockingWeight')}
              {renderNumberInput('monthsFromStocking')}
              {renderNumberInput('numberOfCages')}
            </>
          )}
          <MonthYearSelect
            isHarvesting
            fieldName="harvestingMonthYear"
            activeDate={harvestingMonthYear}
            dateToCompare={stockingMonthYear}
            numberOfMonthBeforeDate={ONE_YEAR_IN_MONTHES}
            numberOfMonthAfterDate={ONE_YEAR_IN_MONTHES * 4}
          />
          {renderNumberInput('harvestingLength')}
          {((isEditMode && canUpdate) || !isEditMode) && (
            <div className="form-group">
              <Field
                required
                name="productFamilyId"
                component={Select}
                label={formatMessage(messages.productFamilyName)}
                formControlProps={{
                  fullWidth: true,
                  required: true,
                }}
                validate={getValidator([VALIDATOR_IS_EMPTY])}
                parse={value => onChangeSelect(value, 'productFamilyId')}>
                {productFamilyList.map(({ id, name }) => (
                  <MenuItem key={id} value={id}>
                    {name}
                  </MenuItem>
                ))}
              </Field>
            </div>
          )}
          <div className="form-button-group">
            <Button
              href={null}
              type="button"
              color="default"
              size="medium"
              variant="contained"
              onClick={onPanelClose}
              className="cancel-event-btn"
              fullWidth={fullWidth}>
              {formatMessage(messages.cancel)}
            </Button>
            <Button
              href={null}
              type="submit"
              color="primary"
              size="medium"
              variant="contained"
              onClick={handleSubmit}
              className="save-event-btn"
              fullWidth={fullWidth}>
              {formatMessage(messages.save)}
            </Button>
          </div>
        </form>
      )}
    />
  )
}

export default connect(
  state => ({
    activeSite: state.getIn(['projectedSitesManager', 'activeSite']),
    clientsList: state.getIn(['projectedSitesManager', 'clientsList']),
    sitesList: state.getIn(['projectedSitesManager', 'sitesList']),
    regionsList: state.getIn(['projectedSitesManager', 'regionsList']),
    speciesList: state.getIn(['projectedSitesManager', 'speciesList']),
    productFamilyList: state.getIn([
      'projectedSitesManager',
      'productFamilyList',
    ]),
    storePsfs: state.getIn(['projectedSitesManager', 'storePsfs']),
  }),
  {
    setActiveSite,
    saveListForm,
    fetchProjectedSitesManagerClients,
    fetchProjectedSitesManagerSites,
    fetchProjectedSitesManagerRegions,
    fetchProjectedSitesManagerSpecies,
    fetchProjectedSitesManagerProductFamily,
    fetchProjectedSitesManagerList,
  },
)(injectIntl(EditForm))
